import type {ChangeEvent} from "react";
import {css, Theme} from "@emotion/react";

import {calculateRemSize, flexAlignCenter, mr} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

export type OptionValueType = boolean | number | string;
export type Value = OptionValueType | [string | number];

interface IProps extends Partial<Omit<HTMLInputElement, "onChange" | "value">> {
    onChange: (event: ChangeEvent<HTMLInputElement>, value: Value) => void;
    options: {label: string; value: OptionValueType}[];
    value: Value;
    className?: string;
}

export const Radio = (props: IProps) => {
    const {options} = props;

    const optionValueType: OptionValueType = typeof options[0]?.value;

    return (
        <div css={group} className={props.className}>
            {options.map((option, index) => {
                const id = `${props.name}_${index}`;
                const value = convertToRadioValue(option.value);

                return (
                    <div key={index} css={radio}>
                        <input
                            onChange={(e) => {
                                props.onChange(e, convertFromRadioValue(e.target.value, props.value, optionValueType));
                            }}
                            id={id}
                            type="radio"
                            value={value}
                            checked={value === convertToRadioValue(props.value)}
                        />

                        <label htmlFor={id}>
                            <Text variant="info_txt_1">{option.label}</Text>
                        </label>
                    </div>
                );
            })}
        </div>
    );
};

/*
 * By default input of type `radio` use string as value type. We need to convert option value to string.
 * And then we need to convert string to the value type. Thanks to this we can sync desired value type
 * with radio input value.
 */
const convertToRadioValue = (value: Value): string => {
    if (Array.isArray(value) && value.length) {
        return value[0].toString();
    }

    return ["boolean", "number"].includes(typeof value) ? Number(value).toString() : (value as string);
};

const convertFromRadioValue = (radioValue: string, value: Value, optionValueType: OptionValueType): Value => {
    if (Array.isArray(value)) {
        return [optionValueType === "number" ? Number(radioValue) : radioValue];
    }

    const valueType = typeof value;

    return valueType === "boolean" ? Boolean(Number(radioValue)) : valueType === "number" ? Number(radioValue) : radioValue;
};

const group = css`
    ${flexAlignCenter};
    gap: ${calculateRemSize(3)};
`;

const radio = (theme: Theme) => css`
    input[type="radio"] {
        position: absolute;
        width: 0;
        height: 0;
        max-width: 0;
        opacity: 0;

        + label {
            ${flexAlignCenter};
            cursor: pointer;

            &:before {
                content: "";
                ${mr(0.5)};
                border-radius: 100%;
                border: 2px solid ${theme.colors.gray[700]};
                width: 2rem;
                height: 2rem;
            }
        }

        &:checked {
            + label {
                &:before {
                    border-color: #02d054;
                    background-color: #02d054;
                    box-shadow: inset 0 0 0 0.2rem #fff;
                }
            }

            &:disabled {
                + label {
                    :before {
                        background-color: ${theme.colors.gray[300]};
                    }
                }
            }
        }

        &:disabled {
            + label {
                cursor: not-allowed;

                &:before {
                    cursor: not-allowed;
                    border-color: ${theme.colors.gray[300]};
                }
            }
        }
    }
`;
